import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiPhone, FiCalendar } = FiIcons;

const ContactSection = () => {
  const contactInfo = [
    {
      icon: FiMail,
      title: "Email",
      value: "hello@wethebillions.com",
      href: "mailto:hello@wethebillions.com"
    },
    {
      icon: FiPhone,
      title: "Call/Text",
      value: "(202) 656-0054",
      href: "tel:+12026560054"
    },
    {
      icon: FiCalendar,
      title: "Book Online",
      value: "wethebillions.com",
      href: "https://wethebillions.neetocal.com/consultation"
    }
  ];

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            Ready to Think Like a Champion?
          </h2>
          
          <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
            Take the first step toward unlocking your mental performance potential. Book your consultation today.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="grid gap-6">
              {contactInfo.map((info, index) => (
                <motion.a
                  key={index}
                  href={info.href}
                  target={info.href.startsWith('http') ? '_blank' : '_self'}
                  rel={info.href.startsWith('http') ? 'noopener noreferrer' : ''}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-center gap-4 p-6 bg-gray-50 rounded-xl hover:bg-accent-light/30 transition-all duration-300 group"
                >
                  <div className="bg-accent-magenta text-white p-3 rounded-full group-hover:bg-accent-magenta/90 transition-colors duration-300">
                    <SafeIcon icon={info.icon} className="h-6 w-6" />
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {info.title}
                    </h3>
                    <p className="text-gray-700 group-hover:text-accent-magenta transition-colors duration-300">
                      {info.value}
                    </p>
                  </div>
                </motion.a>
              ))}
            </div>

            <div className="bg-accent-light/30 p-8 rounded-2xl border border-accent-lavender/30">
              <h3 className="font-playfair text-xl font-bold text-gray-900 mb-4">
                What to Expect in Your Consultation:
              </h3>
              
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-accent-magenta rounded-full mt-2 flex-shrink-0"></div>
                  <span>Comprehensive assessment of your current performance challenges</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-accent-magenta rounded-full mt-2 flex-shrink-0"></div>
                  <span>Personalized executive function training plan</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-accent-magenta rounded-full mt-2 flex-shrink-0"></div>
                  <span>Clear roadmap to peak mental performance</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-accent-magenta rounded-full mt-2 flex-shrink-0"></div>
                  <span>Answers to all your questions about cognitive training</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Booking Iframe */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="p-6 bg-gradient-to-r from-accent-light/50 to-accent-purple/30 border-b border-gray-100">
              <h3 className="font-playfair text-xl font-bold text-gray-900 text-center">
                Book Your Free Consultation
              </h3>
            </div>
            
            <div className="p-2">
              <iframe
                allowFullScreen
                height="600"
                width="100%"
                frameBorder="0"
                title="Book Your Consultation - We The Billions"
                src="https://wethebillions.neetocal.com/embed/5f6e78fe-d1ce-43f6-b1ce-797e0cec501a"
                className="rounded-lg"
              />
            </div>
            
            <div className="p-6 bg-gray-50 text-center">
              <p className="text-sm text-gray-600 mb-3">
                Having trouble with the booking widget?
              </p>
              <a
                href="https://wethebillions.neetocal.com/consultation"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-sm"
              >
                Book Directly Here
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;